import moment from "moment";
import React, { useState, useEffect } from "react";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";

import { useIntl, FormattedMessage, defineMessages } from "react-intl";

import CloseMenuIcon from "../common/icons/closeMenuIcon";
import CloseIcon from "@material-ui/icons/Close";
import Button from "../common/button/button";
import DateTimePicker from "../common/datepicker/dateTimePicker";
import Dropdown from "../common/dropdown/dropdown";
import AccountIcon from "../common/icons/accountIcon";

const messages = defineMessages({
    assignResourceTitle: {
        id: "assign-resource-dialog.title",
        defaultMessage: "Assign resource",
        description: "Assign resource dialog title",
    },
    selectTechnicians: {
        id: "assign-resource-dialog.select-technicians-caption",
        defaultMessage: "Assigned resource",
        description: "Assign resource drop down caption",
    },
    selectStart: {
        id: "assign-resource-dialog.select-start-datetime-caption",
        defaultMessage: "Start date \u0026 time",
        description: "Assign resource start datetime caption",
    },
    dateError: {
        id: "assign-resource-dialog.date-error-msg",
        defaultMessage: "You may not assign a work order outside of working hours",
        description: "Cannot assign work order outside of working hours error message",
    },
});

const styles = (theme) => ({
    title: {
        margin: 0,
        borderTopRightRadius: "10px",
        borderTopLeftRadius: "10px",
        backgroundColor: "var(--blue-bell)",
        height: 54,
        padding: "16px 32px",

        fontFamily: "BellSlimBlack",
        fontStyle: "normal",
        fontWeight: "900",
        fontSize: "18px",
        lineHeight: "22px",
        letterSpacing: "0.0018em",
        color: "var(--text-white)",
        display: "flex",
        justifyContent: "space-between",
    },
    closeButton: {
        color: "var(--text-white)",
        padding: 0,
    },
});

const style = makeStyles({
    linkButton: {
        marginRight: "auto",
        border: "none",
        color: "var(--blue-bell)",
        backgroundColor: "white",
        paddingTop: "5px",
        display: "block",
        "&:focus": {
            border: "none",
            outline: "none",
        },
        "&:disabled": {
            color: "var(--inactive-color)",
        },
    },
});

const DialogTitle = withStyles(styles)((props) => {
    const { children, classes, onClose, ...other } = props;
    return (
        <MuiDialogTitle disableTypography className={classes.title} {...other}>
            <div>{children}</div>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon title="Close Dialog" />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles((theme) => ({
    root: {
        padding: "16px 32px",
    },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
    root: {
        display: "flex",
        justifyContent: "flex-end",
        margin: 0,
        padding: "8px 32px 16px 32px",
    },
}))(MuiDialogActions);

const useStyles = makeStyles((theme) => ({
    paper: {
        borderRadius: "10px",
    },
}));

const CustomizedDialogs = (props) => {
    const [technician, setTechnician] = useState(null);
    const [start, setStart] = useState(null);
    const [dateError, setDateError] = useState(null);
    const [disableBackdropClick, setDisableBackdropClick] = useState(false);

    const classes = useStyles();
    const intl = useIntl();
    // Default to workOrder resourceAssignedTo and date
    useEffect(() => {
        if (props?.workOrder?.resourceAssignedTo) {
            const tech = props.technicians.find((t) => t?.id === props?.workOrder?.resourceAssignedTo);
            setTechnician(tech);
            //setStart(props.workOrder.taskAssignedDate.toDate())
            // setStart(props.workOrder.scheduledDate ? new Date((new Date(props.workOrder.scheduledDate).getTime() + (((new Date()).getTimezoneOffset()) * 60000))) : null)
            setStart(props.workOrder.scheduledDate ? new Date(new Date(props.workOrder.scheduledDate).getTime()) : null);
        } else {
            setTechnician(null);
            setStart(null);
        }
    }, [props.workOrder]);

    const setStartDate = (date) => {
        setStart(date);
    };

    const doAssignTask = () => {
        const startVal = start ? moment.utc(start).add(moment().utcOffset(), "minutes").format("YYYY-MM-DD HH:mm:ss") : "";
        props.onAccept(technician, startVal);
        const startTime = moment(start).subtract({ h: 5 }).format();
        setStart(startTime);
    };

    const doUnassignTask = () => {
        props.onHandleUnassign(technician, start);
        setTechnician(null);
        setStart(null);
    };
    const enabled = technician && start && dateError === null;
    const dateTimer = start && start?.toString()?.includes("Standard") ? new Date(start)?.setHours(new Date(start)?.getHours()) : start;
    return (
        <div>
            <Dialog
                className="cmis-dialog"
                onClose={props.onCancel}
                aria-labelledby="customized-dialog-title"
                open={props.show}
                PaperProps={{
                    className: classes.paper,
                }}
                style={{
                    borderRadius: "10px",
                }}
                disableBackdropClick={disableBackdropClick}>
                <DialogTitle id="customized-dialog-title" onClose={props.onCancel}>
                    {intl.formatMessage(messages.assignResourceTitle)}
                </DialogTitle>
                <DialogContent>
                    <Dropdown
                        id="assigned-resource"
                        label={intl.formatMessage(messages.selectTechnicians)}
                        icon={<AccountIcon title={intl.formatMessage(messages.selectTechnicians)} />}
                        display={(item) => item.firstName + " " + item.lastName}
                        itemValue={(item) => item.id}
                        items={props.technicians}
                        value={technician}
                        onChange={setTechnician}
                    />

                    <DateTimePicker
                        minDate="today"
                        label={intl.formatMessage(messages.selectStart)}
                        value={dateTimer}
                        onChange={setStartDate}
                        error={dateError}
                        width="284px"
                        onOpen={() => setDisableBackdropClick(true)}
                        onClose={() => setDisableBackdropClick(false)}
                    />
                </DialogContent>
                <DialogActions>
                    <button className={style().linkButton} disabled={!props?.workOrder?.resourceAssigned?.id} onClick={() => doUnassignTask()}>
                        <FormattedMessage
                            id="unassign-resource-dialog.assign-resource-button"
                            defaultMessage="Unassign resource"
                            description="Unassign resource accept button caption"
                        />
                    </button>
                    <Button key="0" disabled={!enabled} onClick={doAssignTask}>
                        <FormattedMessage
                            id="assign-resource-dialog.assign-resource-button"
                            defaultMessage="Assign resource"
                            description="Assign resource accept button caption"
                        />
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default CustomizedDialogs;
